import { useEffect, useRef, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'workbook:follow-agent';
const listeners = new Set<() => void>();
/**
 * The choice storage refused to keep (a write that threw), so the toggle still
 * works this visit. It wins over storage until a write succeeds again.
 */
let remembered: boolean | null = null;

/** Storage is the truth (other tabs write it); the memory stands in once a write failed. */
function readFollowing(): boolean {
  if (remembered !== null) return remembered;
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

function setFollowing(follow: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, follow ? 'on' : 'off');
    remembered = null;
  } catch {
    // Storage is unavailable; the choice lasts until the page reloads.
    remembered = follow;
  }
  for (const listener of listeners) listener();
}

/** Whether the page follows the agent's edits: on by default, kept per viewer. */
export function useFollowAgent(): [boolean, (follow: boolean) => void] {
  const following = useSyncExternalStore(subscribe, readFollowing, () => true);
  return [following, setFollowing];
}

/** What follow mode remembers between agent edits. */
interface FollowState {
  /** The tab the agent just opened; the next selection of it is not the user's. */
  expected: string | null;
  /** The user navigated during this agent turn, so the rest of it is theirs. */
  paused: boolean;
  /**
   * The tab shown when an agent edit landed, so a selection change away from it
   * may be the edit's doing (a removed tab). Cleared once a commit leaves the
   * selection there, or the reply changes, so it never excuses a later click.
   */
  editedFrom: string | null;
  /** The tab and busy flag last seen by `trackTurn`. */
  shown: string;
  busy: boolean;
  /** The reply last seen; queued replies run back to back without an idle gap. */
  replyId: string | null;
}

/**
 * Follow mode: `follow` opens the tab an agent edit changed, unless the user
 * switched it off or navigated themselves during this turn (see
 * `useFollowTurn`). `agentOpen` is for the agent's own navigation, which is
 * never mistaken for the user's.
 */
export function useWorkbookFollow({
  selection,
  openItem,
  setFollow,
}: {
  selection: string;
  openItem: (id: string) => void;
  /** Hands `follow` to the editor's agent port. */
  setFollow: (
    follow: (id: string | null, removed?: readonly string[]) => void,
  ) => void;
}) {
  const [following] = useFollowAgent();
  const state = useRef<FollowState>({
    expected: null,
    paused: false,
    editedFrom: null,
    shown: selection,
    busy: false,
    replyId: null,
  });
  const agentOpen = (id: string) => {
    if (id !== latest.current.selection) state.current.expected = id;
    latest.current.openItem(id);
  };
  const latest = useRef({ selection, openItem, following, agentOpen });
  useEffect(() => {
    latest.current = { selection, openItem, following, agentOpen };
    if (state.current.editedFrom === selection) state.current.editedFrom = null;
  });
  useEffect(
    () =>
      setFollow((id, removed = []) => {
        if (id === null) {
          // Only an edit that removed the shown tab moves the selection itself;
          // marking any other edit would excuse a later click.
          if (removed.includes(latest.current.selection)) {
            state.current.editedFrom = latest.current.selection;
          }
          return;
        }
        if (latest.current.following && !state.current.paused) {
          latest.current.agentOpen(id);
        }
      }),
    [setFollow],
  );
  /** Pauses following when the user changes tab while the agent is working, and resumes it at the next turn. */
  const trackTurn = (
    shown: string,
    agentBusy: boolean,
    replyId: string | null = null,
  ) => {
    const turn = state.current;
    if (replyId !== turn.replyId) turn.editedFrom = null;
    if (agentBusy && (!turn.busy || replyId !== turn.replyId)) {
      turn.paused = false;
    }
    turn.busy = agentBusy;
    turn.replyId = replyId;
    if (turn.shown === shown) return;
    turn.shown = shown;
    const agentCaused = turn.editedFrom !== null;
    turn.editedFrom = null;
    if (turn.expected === shown) turn.expected = null;
    else if (agentBusy && !agentCaused) turn.paused = true;
  };
  return { agentOpen, trackTurn };
}

/**
 * Feeds the page's tab and the agent's busy flag to `trackTurn`. Lives in its
 * own component so the streaming reply re-renders it, not the whole page.
 */
export function useFollowTurn(
  trackTurn: (
    selection: string,
    agentBusy: boolean,
    replyId: string | null,
  ) => void,
  selection: string,
  agentBusy: boolean,
  replyId: string | null,
) {
  useEffect(
    () => trackTurn(selection, agentBusy, replyId),
    [trackTurn, selection, agentBusy, replyId],
  );
}
